import { Fragment } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { isMacPlatform } from '@/lib/platform';
import { useMe } from '@/hooks/use-me';
import { useAvivadorBadge } from '@/hooks/use-avivador';
import { Kbd } from '@/components/ui/kbd';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Sidebar as SidebarPrimitive,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import type { Rol } from '@/types/api';
import { NAV_GROUPS, type NavItem } from './nav-items';
import { UserMenu } from './user-menu';
import { ThemeToggle } from './theme-toggle';

// Mismo ranking que RequireRole/RequireAdmin: viewer < gestor < admin.
const ROL_RANK: Record<Rol, number> = { viewer: 0, gestor: 1, admin: 2 };

// Tooltip del badge del Avivador: «72 vivas · 120 olvidadas» (§13 «Badge del
// menú = vivas»), con el singular cuando es 1.
function textoBadgeAvivador(vivas: number, olvidadas: number): string {
  const n = (x: number) => x.toLocaleString('es-AR');
  return `${n(vivas)} ${vivas === 1 ? 'viva' : 'vivas'} · ${n(olvidadas)} ${olvidadas === 1 ? 'olvidada' : 'olvidadas'}`;
}

interface Props {
  onOpenSearch: () => void;
}

// R4b (Ajuste 31 de plan_migracion_reui.md): la Sidebar de shadcn TAL CUAL
// (c-sidebar-2, collapsible="icon") — reemplaza el <aside> a mano de antes.
// El estado expandido/colapsado y su persistencia viven en layout.tsx
// (SidebarProvider controlado); acá solo se consume vía las clases
// `group-data-[collapsible=icon]:…` que ya trae cada pieza del vendorizado.
// Colores: SIEMPRE los tokens --sidebar-* (Sidebar/SidebarMenuButton/etc. ya
// pintan con ellos solos) — nada de zinc-*/white a mano, como tenía el
// <aside> viejo.
export function Sidebar({ onOpenSearch }: Props) {
  const { data: user } = useMe();
  const location = useLocation();
  const mac = isMacPlatform();
  // Badge de conteo del Avivador (design_avivador.md §2 punto 7): «lo del
  // filtro inicial de cada uno» — ya viene resuelto por el server (lo de la
  // encargada logueada, o todo el equipo si no tiene obras propias).
  // Avivador v2 (§13 «Badge del menú = vivas»): cuenta las VIVAS (tope
  // «99+»); el tooltip suma las olvidadas. Oculto si 0 o sin datos
  // (isPending/isError): un 0 en la nav no aporta y un valor viejo mientras
  // carga sería engañoso. SidebarMenuBadge se esconde solo en modo ícono
  // (group-data-[collapsible=icon]:hidden, del vendorizado): en el rail
  // colapsado el conteo se pierde y solo queda el ícono — aceptado (R4b).
  const { data: avivadorBadge } = useAvivadorBadge();
  const vivasAvivador = avivadorBadge?.vivas ?? 0;
  const mostrarBadgeAvivador = vivasAvivador > 0;

  // Cada grupo se filtra por minRol (mismo ranking que RequireRole); un
  // grupo que se queda sin ítems visibles no renderiza ni separador ni label.
  const rol = user?.rol;
  const visibleGroups = NAV_GROUPS.map(group => ({
    ...group,
    items: group.items.filter(item => !item.minRol || (!!rol && ROL_RANK[rol] >= ROL_RANK[item.minRol])),
  })).filter(group => group.items.length > 0);
  // Grupo admin aparte: se pinea al fondo del nav (mt-auto en su propio
  // SidebarGroup, adentro de SidebarContent que ya es flex-1), el resto se
  // apila arriba en su orden de siempre.
  const adminGroup = visibleGroups.find(group => group.key === 'admin');
  const topGroups = visibleGroups.filter(group => group.key !== 'admin');

  function esActivo(item: NavItem): boolean {
    // startsWith a secas marcaba activo cualquier ruta que empiece con el
    // prefijo, sin exigir el límite de segmento ('/gestion-clientes-export'
    // hubiese activado «Gestión»). Exige match exacto o que siga un '/'.
    if (item.to === '/') return location.pathname === '/';
    return location.pathname === item.to || location.pathname.startsWith(item.to + '/');
  }

  function renderNavItem(item: NavItem) {
    const Icon = item.icon;
    const showBadge = item.badge === 'avivador' && mostrarBadgeAvivador;
    const badgeTexto = showBadge ? textoBadgeAvivador(vivasAvivador, avivadorBadge?.olvidadas ?? 0) : '';
    const badgeCount = vivasAvivador > 99 ? '99+' : vivasAvivador;

    return (
      <SidebarMenuItem key={item.to}>
        <SidebarMenuButton
          render={<NavLink to={item.to} end={item.to === '/'} />}
          isActive={esActivo(item)}
          tooltip={showBadge ? `${item.label} · ${badgeTexto}` : item.label}
          // Ítem activo: tint bg-primary/10, SIN barrita de acento (decisión
          // de Juan, B.1 punto 6) — el `data-active` default del vendorizado
          // (bg-sidebar-accent, igual que el hover) no distinguía el activo.
          // Colores de texto e ícono a paridad con la rc.1 (QA automática de la
          // rc.6, 5.3): se habían perdido el gris de los inactivos y el
          // naranja del ícono activo (tokens --sidebar-item* / --sidebar-icon*).
          className="text-sidebar-item hover:text-sidebar-item-hover data-active:bg-primary/10 data-active:text-sidebar-item-active data-active:hover:text-sidebar-item-active"
        >
          {/* Wrapper relative: ancla la mini-pastilla del riel colapsado
              (U-9, qa_visual_reui_2026-09-28.md) sin tocar el ícono. */}
          <span className="relative inline-flex">
            <Icon
              aria-hidden="true"
              className="text-sidebar-icon opacity-85 transition-colors group-hover/menu-button:text-sidebar-icon-hover group-data-active/menu-button:text-primary group-data-active/menu-button:opacity-100 group-data-active/menu-button:group-hover/menu-button:text-primary"
            />
            {showBadge && (
              <span
                aria-hidden="true"
                // Relleno sólido con el número encima → par --primary-solid,
                // no --primary (decisión O3, QA rc.6, Juan, 28/9/2026).
                className="absolute -right-[7px] -top-[7px] hidden h-[13px] min-w-[13px] items-center justify-center rounded-full bg-primary-solid px-[3px] text-[8.5px] font-[650] leading-none tabular-nums text-primary-solid-foreground group-data-[collapsible=icon]:flex"
              >
                {badgeCount}
              </span>
            )}
          </span>
          <span>{item.label}</span>
        </SidebarMenuButton>
        {showBadge && (
          <SidebarMenuBadge
            // El vendorizado (ui/sidebar.tsx) trae peer-hover/menu-button y
            // peer-data-active/menu-button propios apuntando a
            // --sidebar-accent-foreground — sin repetir los mismos
            // modificadores acá, hover/activo le ganaban a text-primary-solid-foreground
            // y el número quedaba casi blanco sobre el naranja (2,4:1, bajo AA).
            // Mismos modificadores + text-primary-solid-foreground para que
            // el par blanco/naranja (decisión O3, QA rc.6, Juan, 28/9/2026,
            // index.css) se sostenga siempre.
            className="rounded-full bg-primary-solid text-primary-solid-foreground tabular-nums peer-hover/menu-button:text-primary-solid-foreground peer-data-active/menu-button:text-primary-solid-foreground"
          >
            {badgeCount}
          </SidebarMenuBadge>
        )}
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarPrimitive collapsible="icon">
      {/* Ritmo vertical del header (logo + buscador) a paridad con la rc.1
          (rc.6, qa_visual_reui_2026-09-28.md U-16 / feedback_paridad_visual_rc1.md):
          pt-4 + min-h-[46px] en la fila del logo + gap-3.5 + pb-3 reproducen
          el pt-4/min-h-[46px]/pb-3.5 de la rc.1 hasta el nav — el p-2/gap-2
          genérico del SidebarHeader vendorizado se quedaba corto. El h-9
          del botón de Buscar (más abajo) completa el resto de la
          diferencia. */}
      <SidebarHeader className="pt-4 gap-3.5 pb-3">
        <div className="flex min-h-[46px] items-center gap-2 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-2.5">
          <img
            src="/ilubaires-logo-dark.svg"
            alt="Ilubaires"
            // U-16 (qa_visual_reui_2026-09-28.md, decisión de Juan 28/9): 44px como en la rc.1, no los 32px del ajuste al SidebarTrigger.
            className="h-11 w-auto select-none group-data-[collapsible=icon]:hidden"
            draggable={false}
          />
          <img
            src="/favicon-128x128.png"
            alt="Ilubaires"
            className="hidden size-8 select-none rounded-sm group-data-[collapsible=icon]:block"
            draggable={false}
          />
          <Tooltip>
            <TooltipTrigger render={<SidebarTrigger className="ml-auto group-data-[collapsible=icon]:ml-0" />} />
            <TooltipContent side="bottom">Mostrar u ocultar el menú</TooltipContent>
          </Tooltip>
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            {/* Decisión 6 de Juan (28/9, qa_visual_reui_2026-09-28.md §7):
                caja con borde, como la rc.1. La sidebar pinta con sus
                propios tokens --sidebar-*, así que el relleno va con
                bg-sidebar-accent (color-mix 7% blanco) y el hover se separa
                a hover:bg-white/10 para no empatar con el hover default del
                botón. h-9 completa el alto de la rc.1 junto con el header
                de arriba. */}
            <SidebarMenuButton
              onClick={onOpenSearch}
              tooltip="Buscar"
              variant="outline"
              className="h-9 bg-sidebar-accent hover:bg-white/10"
            >
              <Search aria-hidden="true" />
              <span>Buscar…</span>
              {/* El Kbd vendorizado (ui/kbd.tsx) está pensado para fondos
                  claros; acá se apaga con los mismos literales que tenía la
                  rc.1 (border-white/15 bg-white/5 text-zinc-400) para no
                  destacar sobre la sidebar, siempre oscura. */}
              <Kbd className="ml-auto border-white/15 bg-white/5 text-zinc-400 group-data-[collapsible=icon]:hidden">
                {mac ? '⌘K' : 'Ctrl K'}
              </Kbd>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* Decisión 7 de Juan (28/9, qa_visual_reui_2026-09-28.md §7): en la
          rc.5 el separador previo a «Gestión» quedaba a todo el ancho (vive
          suelto en SidebarContent, sin el p-2 del SidebarGroup) mientras el
          de «Sistema» y el del pie quedaban con margen (anidados en un
          contenedor con p-2) — mismo componente, dos insets distintos. La
          rc.1 los tenía a todos parejos (mismo margen que el inicio del
          ícono de cada ítem: p-2 del grupo + p-2 del botón = 16px) y el
          borde del pie aparte, más ancho (a la altura del propio p-2 de
          SidebarFooter, sin el margen extra del separador). gap-1 en vez del
          gap-2 default: la rc.1 apilaba los grupos más pegados (U-13).
          mx-4 (O4 de la QA del 28/9, Juan): lo que reproduce la rc.1 no es el
          número (22px = px-3 del <aside> viejo + mx-2.5) sino la relación —
          línea, rótulo e íconos arrancando a la misma altura—; con ReUI el
          contenido quedó a 16px (p-2 del grupo + p-2 del botón/label), y con
          22px la línea arrancaba 6px más adentro que el texto y «flotaba». La
          especificidad del Separator primitivo se resuelve en ui/sidebar.tsx
          (SidebarSeparator). */}
      <SidebarContent role="navigation" aria-label="Navegación principal" className="gap-1">
        {topGroups.map((group, i) => (
          <Fragment key={group.key}>
            {i > 0 && <SidebarSeparator className="mx-4" />}
            {/* py-0 saca el padding vertical de SidebarGroup (U-13) — la
                rc.1 apilaba los ítems sin padding de grupo, solo gap entre
                ellos. px se mantiene para no tocar el inset de 16px del
                ícono (p-2 del grupo + p-2 del botón). */}
            <SidebarGroup className="py-0">
              {group.label && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
              <SidebarGroupContent>
                <SidebarMenu className="gap-0.5">{group.items.map(renderNavItem)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </Fragment>
        ))}

        {adminGroup && (
          <div className="mt-auto flex flex-col">
            <SidebarSeparator className="mx-4" />
            <SidebarGroup className="py-0">
              {adminGroup.label && <SidebarGroupLabel>{adminGroup.label}</SidebarGroupLabel>}
              <SidebarGroupContent>
                <SidebarMenu className="gap-0.5">{adminGroup.items.map(renderNavItem)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>
        )}
      </SidebarContent>

      <SidebarFooter>
        {/* Borde del pie distinto de los separadores de arriba a propósito
            (decisión 7): mx-1 sobre el p-2 de SidebarFooter da los mismos
            12px por lado que la rc.1. */}
        <SidebarSeparator className="mx-1 mb-1" />
        <div className="flex items-center gap-1">
          <UserMenu />
          {/* Colapsado no hay lugar para el toggle de tema (mismo criterio
              que antes: `!collapsed && <ThemeToggle/>`) — se puede cambiar
              el tema desde el pie expandido. */}
          <div className="group-data-[collapsible=icon]:hidden">
            <ThemeToggle />
          </div>
        </div>
      </SidebarFooter>

      <SidebarRail />
    </SidebarPrimitive>
  );
}
